import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/core/context'
import type { ListPage } from '@/services/core/paging'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** A shop whose product feed the network imports. */
export interface Merchant {
  id: number
  title: string
  description: string
  /** The shop's domain — what the API identifies the merchant by when creating ads. */
  link: string
}

/** One product read from a merchant's feed. */
export interface RetailProduct {
  /** Unique within the network: the feed's item id is only unique per merchant. */
  key: string
  merchantId: number
  itemId: string
  title: string
  description: string
  link: string
  imageLink: string
  /** As written in the feed, currency included — not a parsed amount. */
  price: string
  salePrice: string
  brand: string
  colour: string
  gender: string
}

/** A value products can be narrowed by, with how many products carry it. */
export interface ProductFeedFacet {
  value: string
  count: number
}

export interface ProductFeedFacets {
  merchants: Merchant[]
  brands: ProductFeedFacet[]
  colours: ProductFeedFacet[]
  genders: ProductFeedFacet[]
}

export interface RetailProductFilters {
  search?: string
  merchantId?: number
  brand?: string
  colour?: string
}

function mapMerchant(r: Record<string, unknown>): Merchant {
  return {
    id: r.id as number,
    title: (r.title as string) ?? '',
    description: (r.description as string) ?? '',
    link: (r.link as string) ?? '',
  }
}

function mapProduct(r: Record<string, unknown>): RetailProduct {
  const merchantId = r.merchantId as number
  const itemId = (r.itemId as string) ?? ''
  return {
    key: `${merchantId}:${itemId}`,
    merchantId,
    itemId,
    title: (r.itemTitle as string) ?? '',
    description: (r.itemDescription as string) ?? '',
    link: (r.itemLink as string) ?? '',
    imageLink: (r.imageLink as string) ?? '',
    price: (r.price as string) ?? '',
    salePrice: (r.salePrice as string) ?? '',
    brand: (r.brand as string) ?? '',
    colour: (r.colour as string) ?? '',
    gender: (r.gender as string) ?? '',
  }
}

/** Facets with a blank value count products that lack the field; they can't be filtered on. */
function mapFacets(list: unknown): ProductFeedFacet[] {
  return ((list as Record<string, unknown>[] | undefined) ?? [])
    .filter((f) => !!f.facet)
    .map((f) => ({ value: f.facet as string, count: (f.count as number) ?? 0 }))
}

export const ProductFeedApi = {
  /** One page of the network's products, narrowed on the server. The feed has no sort order. */
  getPage: async (
    { search, merchantId, brand, colour }: RetailProductFilters,
    page: number,
    pageSize: number
  ): Promise<ListPage<RetailProduct>> => {
    const params = new URLSearchParams({
      context: getContext(),
      page: String(page),
      pageSize: String(pageSize),
    })
    if (search) params.set('searchString', search)
    if (merchantId !== undefined) params.set('merchant', String(merchantId))
    if (brand) params.set('brand', brand)
    if (colour) params.set('colour', colour)
    const res = await apiFetch(`${API_BASE}/productfeed?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch retail products')
    const data = (await res.json()) as { totalCount?: number }
    const results = toArray<Record<string, unknown>>(data).map(mapProduct)
    return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
  },

  /**
   * The merchants and the values products can be filtered by — null when the
   * network imports no product feed.
   */
  getFacets: async (): Promise<ProductFeedFacets | null> => {
    const res = await apiFetch(`${API_BASE}/productfeed/facets?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (res.status === 404) return null
    if (!res.ok) await throwApiError(res, 'Failed to fetch retail product filters')
    const data = (await res.json()) as Record<string, unknown>
    return {
      merchants: ((data.merchants as Record<string, unknown>[] | undefined) ?? [])
        .filter((m) => m.id != null)
        .map(mapMerchant),
      brands: mapFacets(data.brands),
      colours: mapFacets(data.colours),
      genders: mapFacets(data.genders),
    }
  },

  /**
   * Whether the network has any product to advertise. A user without access
   * to the advertising API is told no rather than shown an error.
   */
  hasProducts: async (): Promise<boolean> => {
    const params = new URLSearchParams({ context: getContext(), page: '1', pageSize: '1' })
    const res = await apiFetch(`${API_BASE}/productfeed/products?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) return false
    const data = (await res.json()) as { totalCount?: number }
    return (data.totalCount ?? 0) > 0
  },

  /**
   * Creates an approved creative advertising one product on the line item,
   * filled in by the server from the product's feed entry using the given
   * retail layout.
   */
  createCreative: async (input: {
    lineItemId: string
    layoutId: string
    merchantLink: string
    itemId: string
  }): Promise<{ id: string; name: string }> => {
    const res = await apiFetch(`${API_BASE}/creatives/retail?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        lineItem: input.lineItemId,
        layout: input.layoutId,
        merchant: input.merchantLink,
        item: input.itemId,
      }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create retail creative')
    const data = (await res.json()) as Record<string, unknown>
    return { id: data.id as string, name: (data.name as string) ?? '' }
  },
}
